import { Link } from 'react-router-dom';

import { LegalEmail, LegalHeading, LEGAL_ENTITY, LegalLayout } from './LegalLayout';
import { Note } from '../../components/ui/Note';
import { BILLING_LIVE, BILLING_PENDING_LEGAL_NOTE, PAID_PLAN_TRIAL, PAID_PLAN_TRIAL_DAYS } from '../../config';

/**
 * The page Stripe's review most often looks for, and the one with the highest
 * chance of being wrong: everything here has to match what the Stripe customer
 * portal actually does when somebody clicks cancel.
 *
 * As built, cancelling in the portal ends the subscription at the end of the
 * paid period. If that setting is changed in the Stripe dashboard, this page
 * becomes false, so the two are worth checking together.
 */
export function RefundsPage() {
    return (
        <LegalLayout title="Cancellation and refunds">
            {!BILLING_LIVE ? <Note>{BILLING_PENDING_LEGAL_NOTE}</Note> : null}

            <p>Cancel any time, no notice period and nobody to email first. This page says what happens to your money.</p>

            <LegalHeading>Plans</LegalHeading>
            <p>
                Free costs nothing and never charges you. Starter, Growth and Scale are monthly subscriptions billed
                through Stripe on a recurring calendar-month cycle, starting the day you upgrade
                {PAID_PLAN_TRIAL
                    ? `, or after a ${PAID_PLAN_TRIAL_DAYS}-day free trial if you qualify (see Trial below)`
                    : ''}
                .
            </p>

            {PAID_PLAN_TRIAL ? (
                <>
                    <LegalHeading>Trial</LegalHeading>
                    <p>
                        Signed-in Free accounts that have never started a paid plan may take one free trial of{' '}
                        {PAID_PLAN_TRIAL_DAYS} days on Starter, Growth or Scale. You add a payment method at Checkout;
                        we do not charge during the trial. When the trial ends, the subscription continues at the plan
                        price unless you cancel first. One trial per account, ever. Cancelling during the trial ends
                        access when the trial ends (or sooner if the portal cancels immediately) and does not charge you.
                        Visitors who start Checkout without signing in, and accounts that already used a trial or paid
                        plan, are billed from day one. Free itself is not a trial and does not expire.
                    </p>
                </>
            ) : null}

            <LegalHeading>Cancelling</LegalHeading>
            <p>
                Cancelling stops the next payment. Your access continues at your current plan to the end of the period
                you've already paid for
                {PAID_PLAN_TRIAL ? ' (or to the end of an unpaid trial, with no charge)' : ''}
                , then drops to Free. We don't pro-rate or refund a partial month by default.
            </p>

            <LegalHeading>How to cancel</LegalHeading>
            <p>
                Sign in to the console and choose <span className="text-text">Manage billing</span>. That opens
                Stripe's portal, where you can cancel or change your card. You can also email us and we'll do it.
                {!BILLING_LIVE
                    ? ' That button appears the day paid plans open; until then there is nothing to cancel.'
                    : ''}
            </p>

            <LegalHeading>When we will refund</LegalHeading>
            <p>
                If the API was materially unavailable for an extended period during a billing cycle through our fault,
                tell us and we'll refund that cycle. Outside that, refunds are at our discretion. We'd rather sort
                out a genuine problem than argue about a month's charge. Ask.
            </p>
            <p>
                This is not a promise about eBay's own data being complete or that anything the API returns predicts
                what will sell. See the{' '}
                <Link to="/terms" className="text-primary underline">
                    terms
                </Link>{' '}
                for what we do and don't claim about the data itself.
            </p>

            <LegalHeading>Contact</LegalHeading>
            <p>
                {LEGAL_ENTITY.name}, <LegalEmail />.
            </p>
        </LegalLayout>
    );
}
